<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex inline-flex items-center text-sm text-gray-500"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="Tenzo Integration" />
            </div>

            <BaseSpinner :isLoading="isLoading" />
        </div>
        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                        >
                            Details
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            Enter your Tenzo SFTP credentials to automatically
                            send customer reviews.
                        </p>
                        <p class="mt-2 text-sm text-gray-500">
                            You'll need a Tenzo account,
                            <a
                                class="text-indigo-600"
                                href="https://www.gotenzo.com/"
                                rel="noopener"
                                target="_blank"
                                >create one here</a
                            >.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0" v-if="!isLoading">
                    <div class="shadow sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="sm:col-span-6">
                                    <base-input-field
                                        v-model="tenzo.host"
                                        type="text"
                                        label="SFTP Host"
                                        id="sftp-host"
                                        placeholder="sftp.gotenzo.com"
                                        :errors="$v.tenzo.host.$error"
                                        errorMessage="This field is required"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <base-input-field
                                        v-model="tenzo.username"
                                        type="text"
                                        label="SFTP Username"
                                        id="sftp-username"
                                        placeholder="Enter your SFTP username"
                                        :errors="$v.tenzo.username.$error"
                                        errorMessage="This field is required"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <base-input-field
                                        v-model="tenzo.password"
                                        type="password"
                                        label="SFTP Password"
                                        id="sftp-password"
                                        placeholder="Enter your SFTP password"
                                        :errors="$v.tenzo.password.$error"
                                        errorMessage="This field is required"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <base-input-field
                                        v-model.number="tenzo.port"
                                        type="number"
                                        label="SFTP Port"
                                        id="sftp-port"
                                        placeholder="22"
                                        caption="Default: 22"
                                    />
                                </div>

                                <div class="sm:col-span-6">
                                    <label
                                        for="enabled"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                    >
                                        Integration Status
                                    </label>

                                    <div class="mt-2 flex items-start">
                                        <div class="w-full" v-auto-animate>
                                            <div
                                                class="flex flex-row items-center"
                                                @click="$v.$touch()"
                                            >
                                                <toggle-switch
                                                    v-model="tenzo.enabled"
                                                    :isDisabled="
                                                        !hasValidConfig
                                                    "
                                                    @toggle-enable="
                                                        toggleEnabled
                                                    "
                                                />
                                                <label
                                                    class="ml-2 text-sm text-gray-500"
                                                >
                                                    {{
                                                        tenzo.enabled
                                                            ? 'Enabled - automatically sending reviews to Tenzo'
                                                            : 'Disabled - not sending reviews to Tenzo'
                                                    }}
                                                </label>
                                            </div>
                                            <p
                                                v-if="!hasValidConfig"
                                                class="mt-1 text-sm text-red-600"
                                            >
                                                Please save the configuration
                                                first before enabling the
                                                integration.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <BaseSpacerWithRuler />

        <div class="my-6 ml-4 flex flex-shrink-0 justify-end">
            <span
                class="inline-flex rounded-md shadow-sm"
                @click="saveSettings"
            >
                <button
                    type="button"
                    class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo focus:outline-none active:bg-indigo-700"
                    :disabled="isLoading"
                >
                    Save
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import ToggleSwitch from '@/components/formComponents/ToggleSwitch';

export default {
    name: 'TenzoSettings',
    components: { ToggleSwitch },
    metaInfo: {
        title: 'Tenzo Integration'
    },
    props: {
        venueId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            hasExistingConfig: false,
            tenzo: {
                host: '',
                username: '',
                password: '',
                port: 22,
                enabled: false
            }
        };
    },
    validations: {
        tenzo: {
            host: {
                required
            },
            username: {
                required
            },
            password: {
                required
            }
        }
    },
    async created() {
        await this.fetchSettings();
    },
    computed: {
        hasValidConfig() {
            return this.hasExistingConfig;
        }
    },
    methods: {
        async fetchSettings() {
            try {
                this.isLoading = true;

                const { data } = await this.$axios.get('/tenzo/config');

                if (data.success && data.config) {
                    this.hasExistingConfig = true;
                    this.tenzo = {
                        host: data.config.host || '',
                        username: data.config.username || '',
                        password: '',
                        port: data.config.port || 22,
                        enabled: data.config.enabled || false
                    };
                } else {
                    this.hasExistingConfig = false;
                    this.tenzo = {
                        host: '',
                        username: '',
                        password: '',
                        port: 22,
                        enabled: false
                    };
                }
            } catch (e) {
                console.error('Error fetching Tenzo settings:', e);
            } finally {
                this.isLoading = false;
            }
        },

        async toggleEnabled() {
            if (!this.hasValidConfig) {
                return;
            }

            try {
                this.tenzo.enabled = !this.tenzo.enabled;

                await this.$axios.post('/tenzo/toggle', {
                    enabled: this.tenzo.enabled
                });

                this.$notify({
                    group: 'settings',
                    title: this.tenzo.enabled
                        ? 'Integration enabled'
                        : 'Integration disabled',
                    type: 'success'
                });
            } catch (error) {
                this.tenzo.enabled = !this.tenzo.enabled;

                this.$notify({
                    group: 'settings',
                    title: 'Failed to update integration status',
                    text:
                        'Please try again or contact support if the problem persists',
                    type: 'error'
                });

                console.error('Tenzo toggle error:', error);
            }
        },

        async saveSettings() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.$notify({
                    group: 'settings',
                    title: 'Please fill in all required fields',
                    type: 'error'
                });
                return false;
            }

            try {
                this.isLoading = true;

                await this.$axios.post('/tenzo/config', {
                    enabled: this.tenzo.enabled,
                    host: this.tenzo.host,
                    username: this.tenzo.username,
                    password: this.tenzo.password,
                    port: this.tenzo.port || 22
                });

                this.hasExistingConfig = true;

                this.$notify({
                    group: 'settings',
                    title: 'Settings saved successfully',
                    type: 'success'
                });
            } catch (error) {
                console.error('Tenzo config save error:', error);

                if (error.response?.data?.message) {
                    this.$notify({
                        group: 'settings',
                        title: 'Failed to save settings',
                        text: error.response.data.message,
                        type: 'error'
                    });
                } else {
                    this.$notify({
                        group: 'settings',
                        title: 'Failed to save settings',
                        text: 'Please check your configuration and try again',
                        type: 'error'
                    });
                }

                this.tenzo.enabled = false;
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>
